import { useApolloClient } from "@apollo/client";
import { useCallback } from "react";

/**
 * Thin, domain-free wrapper around the Apollo client.
 *
 * Everything here is generic cache/network plumbing — it knows nothing about
 * leads, tasks or opportunities. Pass the operation names (or field names) in;
 * keep the lists of them next to the feature that owns them.
 *
 * @returns {{
 *   client: object,
 *   refetchQueries: (names: string|string[]) => Promise,
 *   evictField: (fieldName: string, options?: object) => boolean,
 *   evictObject: (ref: object) => boolean,
 *   resetStore: () => Promise,
 * }}
 */
const useApollo = () => {
  const client = useApolloClient();

  /**
   * Refetch active queries by OPERATION name (`query getFoo(...)`, not the
   * exported const).
   *
   * Fire-and-forget: callers invalidate AFTER a mutation has succeeded and the
   * user has been told so, and a failed refresh must not surface as a second
   * error. It is logged, and the stale view corrects itself on the next fetch.
   */
  const refetchQueries = useCallback(
    names => {
      const include = (Array.isArray(names) ? names : [names]).filter(Boolean);
      if (include.length === 0) return Promise.resolve([]);
      return client
        .refetchQueries({ include })
        .catch(err => console.error(`refetch failed for ${include.join(", ")}`, err));
    },
    [client]
  );

  /** Drop a ROOT_QUERY field so the next read misses and refetches. */
  const evictField = useCallback(
    (fieldName, options = {}) => {
      const evicted = client.cache.evict({ id: "ROOT_QUERY", fieldName, ...options });
      client.cache.gc();
      return evicted;
    },
    [client]
  );

  /** Drop a single normalised object (anything `cache.identify` can resolve). */
  const evictObject = useCallback(
    ref => {
      const id = client.cache.identify(ref);
      if (!id) return false;
      const evicted = client.cache.evict({ id });
      client.cache.gc();
      return evicted;
    },
    [client]
  );

  const resetStore = useCallback(
    () => client.resetStore().catch(err => console.error("resetStore failed", err)),
    [client]
  );

  return { client, refetchQueries, evictField, evictObject, resetStore };
};

export default useApollo;
